import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, PaletteIcon, PencilRulerIcon, ShieldCheckIcon, ShuffleIcon, Volume2Icon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ARTE PRONTA™ (Italia). Fonte: arte-pronta/PLAN_2026-10-20.md — promessa = consegna.
// Pacchetto: Volume NUOVO «L'Anno Pronto» + Primaria Vol. 01–05 + Secondaria Vol. 06–08 = 9 volumi, oltre 500 pagine, 5 bonus.
// (Il file mantiene il nome storico "religione.ts" perché i componenti lo importano così.)
export const PRODUCT_NAME = 'ARTE PRONTA™';
export const heroGuarantees = ['Download digitale immediato', 'Pagamento unico', 'Nessun abbonamento', 'Garanzia di 7 giorni'];
export const stats = [{
  value: '500+',
  label: 'Oltre 500 pagine pronte da usare'
}, {
  value: '9',
  label: 'Volumi digitali di Arte e Immagine'
}, {
  value: '8',
  label: 'Classi: Secondaria 1ª–3ª e Primaria 1ª–5ª'
}, {
  value: '12',
  label: 'Lezioni minuto per minuto nel Volume NUOVO'
}, {
  value: '5',
  label: 'Bonus inclusi'
}];
export const differenceStats = [{
  value: 'Secondaria e Primaria',
  label: 'Dalla 1ª Primaria alla 3ª Media: un volume per ogni classe'
}, {
  value: '9 volumi digitali',
  label: 'Programmazione, linguaggio visivo, storia dell\'arte, tecniche, esame di 3ª media'
}, {
  value: 'Classe sotto controllo',
  label: 'Regole del laboratorio, routine e gestione dei materiali già pronte'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'La domenica sera sparisce',
  text: 'Ore davanti al computer a preparare le lezioni di arte della settimana – e non hai ancora finito.'
}, {
  title: 'Il laboratorio diventa caos',
  text: 'Colori, pennelli, acqua e 25 ragazzi: senza regole e routine chiare l\'ora di arte si trasforma in confusione.'
}, {
  title: 'Ore a cercare materiale',
  text: 'Schede, immagini e attività trovate su siti diversi: troppo facili, troppo difficili o da adattare prima di usarle.'
}, {
  title: 'Storia dell\'arte da preparare da zero',
  text: 'Opere, artisti, lettura dell\'immagine e domande: quello che dovrebbe richiedere minuti diventa un lavoro lungo.'
}, {
  title: 'Valutare l\'arte è difficile',
  text: 'Valutare elaborati con criteri chiari, senza soggettività, richiede rubriche che nessuno ti ha mai dato.'
}, {
  title: 'Ansia prima della prossima lezione',
  text: 'Quando il tempo non basta, si finisce per improvvisare e mettere insieme il materiale all\'ultimo minuto.'
}];
export const afterItems = ['Un sistema di Arte e Immagine organizzato per Secondaria e Primaria', 'Programmazione annuale per la 1ª, 2ª e 3ª Media', '12 lezioni minuto per minuto pronte da usare', 'La classe sotto controllo: regole, routine e gestione del laboratorio', 'Schede operative da stampare subito', 'Linguaggio visivo, storia dell\'arte e tecniche già pronti', 'Preparazione all\'esame di 3ª Media', 'Rubriche e griglie di valutazione', 'Un volume per ogni classe della Primaria (1ª–5ª)'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'La domenica va via a preparare lezioni',
  text: 'Passi parte del fine settimana a preparare le lezioni di arte della settimana successiva?'
}, {
  icon: Volume2Icon,
  title: 'La classe non ti segue in laboratorio',
  text: 'Ti capita di passare metà dell\'ora a riportare il silenzio, distribuire materiali e pulire?'
}, {
  icon: FolderSearchIcon,
  title: 'Non sai quale attività usare',
  text: 'Cerchi continuamente schede, opere e attività adatte all\'età e alla classe dei tuoi alunni?'
}, {
  icon: PencilRulerIcon,
  title: 'Vorresti smettere di creare tutto da zero',
  text: 'Vorresti che lezioni, schede, verifiche e rubriche fossero già pronte?'
}, {
  icon: ShuffleIcon,
  title: 'I ragazzi hanno bisogno di varietà',
  text: 'Vorresti alternare tecniche, laboratori e lettura dell\'opera senza perdere ore a progettarli?'
}, {
  icon: LayoutGridIcon,
  title: 'Ti serve una struttura più chiara',
  text: 'Vorresti tutto il materiale raccolto in un unico sistema, per trovare subito quello che ti serve?'
}];

export const volumes: Array<{
  number: string;
  level: string;
  classes: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volume NUOVO',
  level: 'Anno',
  classes: 'Secondaria 1ª–3ª',
  title: 'L\'Anno Pronto: Programmazione + 12 Lezioni',
  tagline: 'Tutto l\'anno di Arte e Immagine organizzato, con la classe sotto controllo.',
  intro: 'Il volume nuovo per la Secondaria di I grado: programmazione annuale per la 1ª, 2ª e 3ª Media e 12 lezioni spiegate minuto per minuto, con le regole e le routine per gestire il laboratorio.',
  items: ['Programmazione annuale 1ª, 2ª e 3ª Media', '12 lezioni minuto per minuto', 'Regole e routine del laboratorio di arte', 'Gestione dei materiali in classe', 'Schede operative da stampare', 'Rubriche di valutazione'],
  outro: 'Il punto di partenza: apri, scegli la lezione, entra in aula.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Primaria · Vol. 01',
  level: '1ª',
  classes: 'Primaria · Classe 1ª',
  title: 'Classe 1ª Primaria',
  tagline: 'Segni, colori e forme per i più piccoli.',
  intro: 'Il percorso di arte per la classe 1ª della Primaria, con laboratori e schede pronte.',
  items: ['Lezioni pronte', 'Laboratori creativi', 'Schede operative', 'Tecniche passo per passo'],
  outro: 'Materiale pronto per la classe 1ª.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Primaria · Vol. 02',
  level: '2ª',
  classes: 'Primaria · Classe 2ª',
  title: 'Classe 2ª Primaria',
  tagline: 'Esplorare colore e forma.',
  intro: 'Il percorso di arte per la classe 2ª della Primaria, con laboratori e schede pronte.',
  items: ['Lezioni pronte', 'Laboratori creativi', 'Schede operative', 'Tecniche passo per passo'],
  outro: 'Materiale pronto per la classe 2ª.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Primaria · Vol. 03',
  level: '3ª',
  classes: 'Primaria · Classe 3ª',
  title: 'Classe 3ª Primaria',
  tagline: 'Tecniche e prime opere d\'arte.',
  intro: 'Il percorso di arte per la classe 3ª della Primaria, con laboratori e schede pronte.',
  items: ['Lezioni pronte', 'Laboratori creativi', 'Schede operative', 'Lettura di immagini'],
  outro: 'Materiale pronto per la classe 3ª.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Primaria · Vol. 04',
  level: '4ª',
  classes: 'Primaria · Classe 4ª',
  title: 'Classe 4ª Primaria',
  tagline: 'Arte, storia e tecniche.',
  intro: 'Il percorso di arte per la classe 4ª della Primaria, con laboratori e schede pronte.',
  items: ['Lezioni pronte', 'Laboratori creativi', 'Schede operative', 'Lettura di immagini'],
  outro: 'Materiale pronto per la classe 4ª.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Primaria · Vol. 05',
  level: '5ª',
  classes: 'Primaria · Classe 5ª',
  title: 'Verso il Moderno',
  tagline: 'Artisti, avanguardie e laboratori creativi.',
  intro: 'Per la classe 5ª: colore, luce, movimento, cubismo, astrattismo e collage.',
  items: ['Colore e luce', 'Movimento', 'Cubismo', 'Astrattismo', 'Collage', 'Schede operative'],
  outro: 'Il ponte perfetto verso la Secondaria.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Secondaria · Vol. 06',
  level: '1ª M',
  classes: 'Secondaria · 1ª Media',
  title: 'Linguaggio Visivo',
  tagline: 'Composizione, geometria, colore e lettura dell\'immagine.',
  intro: 'Per la 1ª Media: gli elementi del linguaggio visivo con schede operative pronte.',
  items: ['Punto, linea, forma', 'Colore', 'Composizione', 'Geometria', 'Lettura dell\'immagine', 'Schede operative'],
  outro: 'Le basi del linguaggio visivo, pronte da usare.',
  spine: 'bg-purple-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Secondaria · Vol. 07',
  level: '2ª M',
  classes: 'Secondaria · 2ª Media',
  title: 'Rinascimento e Barocco',
  tagline: 'Artisti, tecniche, prospettiva e lettura dell\'opera.',
  intro: 'Per la 2ª Media: il Rinascimento e il Barocco con schede e lettura guidata delle opere.',
  items: ['Prospettiva', 'Proporzione', 'Artisti', 'Tecniche', 'Lettura dell\'opera', 'Schede operative'],
  outro: 'La storia dell\'arte spiegata in modo chiaro.',
  spine: 'bg-teal-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Secondaria · Vol. 08',
  level: '3ª M',
  classes: 'Secondaria · 3ª Media',
  title: 'Il Novecento e Oltre',
  tagline: 'Avanguardie, arte contemporanea e preparazione all\'esame.',
  intro: 'Per la 3ª Media: avanguardie, arte contemporanea, comunicazione visiva e preparazione all\'esame.',
  items: ['Avanguardie', 'Arte contemporanea', 'Comunicazione visiva', 'Preparazione all\'esame di 3ª Media', 'Schede operative'],
  outro: 'Accompagni la classe fino all\'esame.',
  note: 'Materiale didattico indipendente: non è una pubblicazione ufficiale del Ministero dell\'Istruzione e del Merito.',
  spine: 'bg-pink-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '9 volumi: 1 nuovo + 5 per la Primaria + 3 per la Secondaria',
  text: 'Un sistema organizzato con programmazione, lezioni pronte, schede da stampare e rubriche per tutte le classi di Arte e Immagine. Apri il volume, scegli la lezione e stampa. Meno preparazione. Più tempo per i tuoi alunni.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lezioni pronte',
  text: '12 lezioni minuto per minuto nel Volume NUOVO e lezioni pronte in ogni volume: niente più foglio bianco.'
}, {
  icon: ShieldCheckIcon,
  title: 'Classe sotto controllo',
  text: 'Regole del laboratorio, routine e gestione dei materiali: l\'ora di arte resta ordinata dall\'inizio alla fine.'
}, {
  icon: FileTextIcon,
  title: 'Schede da stampare',
  text: 'Schede operative da stampare e usare subito – per il lavoro individuale, il ripasso e l\'approfondimento.'
}, {
  icon: PaletteIcon,
  title: 'Tecniche e storia dell\'arte',
  text: 'Tecniche passo per passo, artisti e lettura guidata delle opere, dal Rinascimento al Novecento.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Valutazione chiara',
  text: 'Rubriche e griglie per valutare gli elaborati con criteri chiari, più il Registro Voti nei bonus.'
}, {
  icon: CalendarCheckIcon,
  title: 'Programmazione annuale',
  text: 'Per la 1ª, 2ª e 3ª Media nel Volume NUOVO, più un volume per ogni classe della Primaria.'
}];
export const pageTypes = ['Lezioni pronte', 'Schede operative', 'Tecniche passo per passo', 'Lettura dell\'opera'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Registro Voti & Valutazione Rapida',
  text: 'Il sistema per valutare gli elaborati di arte in pochi minuti, con criteri chiari.',
  items: ['Griglie di valutazione', 'Criteri pronti', 'Registro organizzato', 'Meno burocrazia'],
  benefit: 'Valuti di più in meno tempo.'
}, {
  title: 'Kit Inclusione (DSA, BES e DVA)',
  text: 'Strategie pratiche per un\'ora di arte davvero inclusiva.',
  items: ['Strategie per DSA', 'Attività per BES', 'Adattamenti per DVA', 'Idee per lavorare insieme'],
  benefit: 'Ogni alunno partecipa all\'ora di arte.'
}, {
  title: 'Manuale della LIM & Didattica Digitale',
  text: 'Come usare la LIM per portare le opere d\'arte in classe.',
  items: ['La LIM nella lezione di arte', 'Opere in grande formato', 'Attività interattive', 'Idee pronte'],
  benefit: 'La classe diventa un museo digitale.'
}, {
  title: 'Banca del Tempo — 100 Attività da 10 Minuti',
  text: '100 attività rapide per ogni imprevisto: fine lezione, supplenze, classe agitata.',
  items: ['100 attività da 10 minuti', 'Per ogni occasione', 'Zero preparazione', 'Pronte da usare'],
  benefit: 'Mai più senza idee.'
}, {
  title: 'Kit "Profe-Genitori" Sereno',
  text: 'Comunicare con le famiglie e valorizzare il tuo lavoro.',
  items: ['Comunicazioni pronte', 'Colloqui con le famiglie', 'Presentare il lavoro della classe', 'Modelli da usare'],
  benefit: 'Famiglie informate, docente sereno.'
}];
export const BONUS_VALUE = '19 €';
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Apri ARTE PRONTA™',
  text: 'Scarica il pacchetto e apri il volume adatto alla tua classe.'
}, {
  title: 'Scegli lezione e materiale',
  text: 'Trova l\'argomento giusto e scegli le schede, le tecniche o le verifiche adatte ai tuoi alunni.'
}, {
  title: 'Preparati in 5 minuti',
  text: 'Leggi la lezione minuto per minuto e prepara i materiali indicati.'
}, {
  title: 'Stampa e insegna',
  text: 'Stampa le schede o usa il materiale alla LIM – ed entra in aula con tutto pronto.'
}];
export const audienceItems = ['Insegni Arte e Immagine nella Secondaria di primo grado', 'Insegni arte nella Primaria', 'Vuoi una classe più tranquilla e ordinata in laboratorio', 'Vuoi smettere di creare ogni scheda da zero', 'Ti servono lezioni di storia dell\'arte già pronte', 'Vuoi tecniche spiegate passo per passo', 'Vuoi rubriche e griglie di valutazione già pronte', 'Vuoi entrare in aula più preparato'];
export const timeValueTasks = ['Cercare opere e attività adatte', 'Scrivere schede ed esercizi', 'Preparare le lezioni di storia dell\'arte', 'Organizzare materiali e laboratorio', 'Preparare verifiche e rubriche'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '9 volumi digitali – oltre 500 pagine',
  detail: 'Volume NUOVO «L\'Anno Pronto» con programmazione e 12 lezioni per la Secondaria, 5 volumi per la Primaria (1ª–5ª), 3 volumi per la Secondaria: linguaggio visivo, Rinascimento e Barocco, Novecento ed esame'
}, {
  label: '5 bonus senza costi aggiuntivi',
  detail: 'Registro Voti, Kit Inclusione, Manuale LIM, 100 attività da 10 minuti, Kit Profe-Genitori'
}, {
  label: 'Secondaria 1ª–3ª e Primaria 1ª–5ª',
  detail: 'Lezioni pronte, schede da stampare, rubriche di valutazione'
}];
export const offerIncludes = ['Download digitale immediato', 'File PDF da salvare', 'Schede da stampare', 'Nessun prodotto fisico spedito'];
export const PRICE = '19 €';
export const PRICE_OLD = '97 €';

// Solo recensioni autentiche di ARTE PRONTA. La sezione resta nascosta finché è vuota.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Come ricevo il materiale?',
  a: 'ARTE PRONTA™ è un prodotto digitale. Dopo l\'acquisto ricevi via e-mail il link alla cartella con tutti i PDF da scaricare e salvare. Puoi usarli su computer, tablet o LIM e stampare le schede che ti servono.'
}, {
  q: 'È adatto alla scuola italiana?',
  a: 'Sì. Il materiale è pensato per Arte e Immagine nella Secondaria di primo grado e per l\'arte nella Primaria. È un materiale didattico indipendente, non una pubblicazione ufficiale del Ministero.'
}, {
  q: 'Per quali classi è?',
  a: 'Per la Secondaria di primo grado (1ª–3ª Media) e la Primaria (1ª–5ª). Il Volume NUOVO «L\'Anno Pronto» contiene la programmazione annuale e 12 lezioni per la Secondaria; c\'è poi un volume per ogni classe.'
}, {
  q: 'Cosa significa "classe sotto controllo"?',
  a: 'Nel Volume NUOVO «L\'Anno Pronto» trovi regole e routine per il laboratorio di arte, la gestione dei materiali e lezioni strutturate minuto per minuto, così l\'ora resta ordinata dall\'inizio alla fine.'
}, {
  q: 'Posso davvero preparare una lezione in 5 minuti?',
  a: 'ARTE PRONTA™ è pensato per rendere la preparazione più veloce. Apri un volume, scegli una lezione o una scheda pronta e la rivedi in circa cinque minuti. Il tempo può variare in base alla lezione, alla classe e agli adattamenti necessari.'
}, {
  q: 'Ci sono schede pronte da stampare?',
  a: 'Sì. Il materiale contiene schede operative da stampare con attività, esercizi e tecniche passo per passo.'
}, {
  q: 'Devo stampare tutto?',
  a: 'No. Scegli tu quali pagine stampare e puoi usare parte del materiale in digitale o alla LIM.'
}, {
  q: 'I 5 bonus sono inclusi?',
  a: `Sì. Tutti e cinque i bonus sono inclusi senza costi aggiuntivi – tutto a ${PRICE}.`
}, {
  q: 'È un abbonamento?',
  a: 'No. Paghi una sola volta e ricevi l\'accesso al materiale digitale. Nessun costo ricorrente.'
}, {
  q: 'C\'è una garanzia?',
  a: 'Sì. ARTE PRONTA™ ha una garanzia di soddisfazione di 7 giorni. Se il materiale non soddisfa le tue aspettative, puoi contattarci e chiedere il rimborso entro il periodo di garanzia, secondo le condizioni.'
}];
